import {
  KEAP_FAMILY_ANNUAL_CODES,
  KEAP_FAMILY_CODES,
  SOCIAL_ADS_BNI_PLAN_CODES,
} from '../../../utils/constants'
import {PARTNER_VALUES} from '../../../accountManagement/features/AccountInfo/config/locationFieldRules'

export const MARKET_PLACE_CODE = 'appmarket-5-thryv-m2m'

const resolveRepNetworkId = repInfo => {
  if (repInfo?.FederationIdentifier) return repInfo.FederationIdentifier
  const email = String(repInfo?.Email || '').trim()
  return email.toLowerCase().endsWith('@thryv.com')
    ? email.slice(0, -'@thryv.com'.length)
    : undefined
}

/**
 * Builds the sales/partner attribution fields for the cc/order/send-link
 * payload. Shared between ClientProposalFlow's client-payment.js (source:
 * proposalInfo, fetched fresh from GET proposals/:id — see
 * useEnsureProposalInfo — which already has flat behalfRepId/overrideRepId/
 * etc., the shape useProposalSave.js POSTs when the proposal was saved) and
 * core OOE's buildSendLinkPayload.js (source: PurchaseLinkSlice's
 * salesAttributionFields, which only ever has the raw, never-saved
 * salesRepInfo/overrideRepInfo async-search objects — see AccountEditModal).
 * Reads the flat fields directly when already present, else falls back to
 * flattening salesRepInfo.repInfo/overrideRepInfo.repInfo the same way
 * useProposalSave.js's identically-named local helper does.
 *
 * @param {object} source - proposalInfo or salesAttributionFields
 * @returns {object} {salesRepOrPartner, behalfRepId?, behalfFirstName?,
 *   behalfLastName?, behalfEmail?, xpCode?, overrideRepId?,
 *   overrideRepFirst?, overrideRepLast?, overrideRepEmail?} — the
 *   behalf-rep fields are included only when salesRepOrPartner is
 *   'sales-rep', xpCode only when it's one of PARTNER_VALUES, and the
 *   override-rep fields only when overrideSST is 'yes'
 */
export function buildSalesAttributionFields(source) {
  const {
    salesRepOrPartner,
    xpCode,
    overrideSST,
    salesRepInfo,
    overrideRepInfo,
  } = source ?? {}

  return {
    salesRepOrPartner,

    ...(salesRepOrPartner === 'sales-rep' && {
      behalfRepId:
        source?.behalfRepId ||
        resolveRepNetworkId(salesRepInfo?.repInfo) ||
        resolveRepNetworkId({Email: source?.behalfEmail}),
      behalfFirstName:
        source?.behalfFirstName ?? salesRepInfo?.repInfo?.FirstName,
      behalfLastName: source?.behalfLastName ?? salesRepInfo?.repInfo?.LastName,
      behalfEmail: source?.behalfEmail ?? salesRepInfo?.repInfo?.Email,
    }),

    ...(PARTNER_VALUES.includes(salesRepOrPartner) && {
      xpCode: xpCode ?? '',
    }),

    ...(overrideSST === 'yes' && {
      overrideRepId:
        source?.overrideRepId ?? overrideRepInfo?.repInfo?.FederationIdentifier,
      overrideRepFirst:
        source?.overrideRepFirst ?? overrideRepInfo?.repInfo?.FirstName,
      overrideRepLast:
        source?.overrideRepLast ?? overrideRepInfo?.repInfo?.LastName,
      overrideRepEmail:
        source?.overrideRepEmail ?? overrideRepInfo?.repInfo?.Email,
    }),
  }
}

const formatCenters = (list, filterFn) =>
  list
    .filter(filterFn)
    .map(({code, quantity = 1, entitlements, startDate, isAlreadyAdded}) => ({
      code,
      quantity,
      ...(startDate && {startDate}),
      ...(isAlreadyAdded && {isAlreadyAdded: true}),
      addons: (entitlements ?? []).map(({code, quantity, isAlreadyAdded}) => ({
        code,
        quantity: quantity ?? 1,
        ...(isAlreadyAdded && {isAlreadyAdded: true}),
      })),
    }))

const formatApps = (list, filterFn) =>
  list.filter(filterFn).map(({code, quantity = 1, isAlreadyAdded}) => ({
    code,
    quantity,
    ...(isAlreadyAdded && {isAlreadyAdded: true}),
    addons: [],
  }))

export const resolveChildQuantity = ({
  code,
  quantity = 1,
  configuration = {},
  isAlreadyAdded,
}) => {
  const optionValue = configuration?.optionValue
  if (optionValue) {
    const parsed = parseInt(optionValue.split('-')[0], 10)
    if (!isNaN(parsed)) {
      return {
        code,
        quantity: parsed,
        ...(isAlreadyAdded && {isAlreadyAdded: true}),
      }
    }
  }
  return {code, quantity, ...(isAlreadyAdded && {isAlreadyAdded: true})}
}

// Reads a named key from both the plain-object format {key: value} written
// by TLAddonPanel/configuredOptions and the [{name, value}] array format
// produced by mapCustomFields for Recurly-seeded items.
// Cart items store a picked tier (Keap Additional Contacts / Text & Voice) as
// configuredOptions.optionValue ("<qty>-<price>") while item.quantity stays 1.
// Every payload that tells Recurly or ooecp a quantity must resolve it here.
export const resolveCartItemQuantity = item =>
  resolveChildQuantity({
    code: item.productCode,
    quantity: item.quantity ?? 1,
    configuration: item.configuredOptions ?? item.configuration ?? {},
  }).quantity

// Carried-over Recurly quantity wins; otherwise the picked option tier (e.g.
// KCP "299-299" → 299 units at $1.00) beats the placeholder item.quantity of 1.
const resolveMappedQuantity = item =>
  item.recurlyQuantity ?? resolveCartItemQuantity(item)

const readConfigField = (configuration, key) => {
  if (Array.isArray(configuration)) {
    return configuration.find(f => f.name === key)?.value
  }
  return configuration?.[key]
}

/**
 * Maps a flat list of proposal-shaped items (productType/productCode/
 * configuredOptions/parentProduct) into the normalized {centers, apps,
 * oneTimes} shape that buildSubscriptionGroups expects.
 *
 * Shared between the Proposal Tool and core OOE's send-link flow
 * (buildSendLinkPayload.js) — both hold their location items in this same
 * flat productType/configuredOptions/parentProduct shape and reach this
 * mapper via buildSendlinkSubscriptionGroups below.
 *
 * @param {Array} items - flat items (productType/productCode/
 *   configuredOptions/parentProduct)
 * @returns {{centers: Array, apps: Array, oneTimes: Array}} normalized cart:
 *   - centers: CENTER items, always including the marketplace center and a
 *     cc_* center (defaults injected if missing from the items)
 *   - apps: APP items, with parentProduct preserved for parent/child nesting
 *   - oneTimes: ONE_TIME_FEE items as {code, quantity}
 */
export function mapItemsToCentersApps(items) {
  let centers = items
    ?.filter(item => item.productType === 'CENTER')
    .map(item => ({
      code: item.productCode,
      name: item.productName || '',
      quantity: resolveMappedQuantity(item),
      configuration: item.configuredOptions ?? item.configuration ?? {},
      isAlreadyAdded: item.alreadyIncluded ?? false,
      // proposal addons → entitlements so formatCenters works unchanged
      entitlements: (item.addons ?? [])
        .filter(addon => addon.productCode)
        .map(addon => ({
          code: addon.productCode,
          quantity: addon.quantity || 1,
          isAlreadyAdded: addon.alreadyIncluded ?? false,
        })),
    }))

  let apps = items
    ?.filter(item => item.productType === 'APP')
    .map(item => ({
      code: item.productCode,
      name: item.productName || '',
      quantity: resolveMappedQuantity(item),
      configuration: item.configuredOptions ?? item.configuration ?? {},
      parentProduct: item.parentProduct ?? null,
      isAlreadyAdded: item.alreadyIncluded ?? false,
      addons: (item.addons ?? [])
        .filter(addon => addon.productCode)
        .map(addon => ({
          code: addon.productCode,
          quantity: addon.quantity || 1,
          isAlreadyAdded: addon.alreadyIncluded ?? false,
        })),
    }))

  if (!centers.some(({code}) => code === MARKET_PLACE_CODE)) {
    centers.push({
      code: MARKET_PLACE_CODE,
      name: 'Thryv Add-Ons',
      quantity: 1,
      addons: [],
    })
  }

  if (!centers.some(item => item.code.startsWith('cc_'))) {
    centers.push({
      code: 'cc_basicpls-5-thryv-m2m',
      name: 'Thryv My Account Basic+',
      quantity: 1,
      entitlements: [],
    })
  }

  const oneTimes = items
    ?.filter(item => item.productType === 'ONE_TIME_FEE')
    .map(item => ({code: item.productCode, quantity: item.quantity || 1}))

  return {centers, apps, oneTimes}
}

/**
 * Groups a normalized {centers, apps, oneTimes} cart into the full
 * cc/order/send-link payload fields, applying all send-link nesting rules:
 *   - cc_* centers → parentSubscriptions, everything else → childSubscriptions
 *   - Keap Add-Ons wrapper (monthly/annual) nesting
 *   - marketplace (appmarket) addon merging/deduping
 *   - standalone app handling (Thryv Leads, AEO & SEO, legacy SEO, vertical
 *     add-ons, etc.)
 *   - mcDomain / keapDomain / keapTrailId / tlDomain / seoProposalId /
 *     seoDomain / thryvLeadsId / cohortEaid derivation
 *
 * Shared between core OOE's send-link flow (buildSendLinkPayload.js) and
 * the Proposal Tool — both reach this via mapItemsToCentersApps +
 * buildSendlinkSubscriptionGroups below, since both hold their location
 * items in the same flat productType/configuredOptions shape.
 *
 * @param {object} cart
 * @param {Array} cart.centers
 * @param {Array} cart.apps
 * @param {Array} cart.oneTimes
 * @returns {object} send-link payload:
 *   - parentSubscriptions: cc_* centers
 *   - childSubscriptions: every other center/app, with Keap Add-Ons,
 *     marketplace addons, and standalone apps nested per the rules above
 *   - oneTimeFees: {code, quantity} for each one-time item
 *   - subscriptionCustomFields: {isCohort, keapAccount, keapId,
 *     isInvoiceBilled} (fixed defaults) plus mcDomain / keapDomain /
 *     keapTrailId / tlDomain / seoProposalId / seoDomain / thryvLeadsId /
 *     cohortEaid, each included only when derivable from the cart — kept
 *     as its own nested object (rather than spread at the top level) so
 *     callers can pass it through to locationDetails without any new
 *     top-level field silently being swept in as a "custom field"
 */
export function buildSubscriptionGroups({centers, apps, oneTimes}) {
  // ── parentSubscriptions: cc_* centers ────────────────────────────────
  const parentSubscriptions = formatCenters(centers, ({code}) =>
    code.startsWith('cc_'),
  )

  // ── childSubscriptions: non-cc centers ───────────────────────────────
  let childSubscriptions = []
  for (const center of centers) {
    if (center.code.startsWith('cc_')) continue
    const formatted = formatCenters(centers, ({code}) => code === center.code)
    childSubscriptions.push(formatted[0])
  }

  // GEP-6803: strip BNI (read-only display, not managed via OOE)
  // seobase-5-thryv-bni-nt-m2m carries its existing Recurly quantity and must
  // remain in the payload as a standalone subscription — exclude it from the strip.
  apps = apps.filter(
    ({code}) =>
      !SOCIAL_ADS_BNI_PLAN_CODES.includes(code) ||
      code === 'seobase-5-thryv-bni-nt-m2m',
  )

  const marketplace = centers.find(({code}) => code === 'appmarket-5-thryv-m2m')

  // ── Parent-child subscriptions ────────────────────────────────────────
  // Apps whose parentProduct matches another app in the cart are children
  // and must be nested under the parent's addons, not sent as standalone
  // subscriptions. parentProduct is sourced from products.parent_prod_code
  // via the proposal items API.
  const appCodes = new Set(apps.map(({code}) => code))
  const appChildToParentMap = new Map(
    apps
      .filter(({parentProduct}) => parentProduct && appCodes.has(parentProduct))
      .map(({code, parentProduct}) => [code, parentProduct]),
  )

  const keapAddonsWrapperMonthly = apps.find(
    ({code}) =>
      code === 'addon-5-keap-addons' ||
      code === 'addon-5-keap-addons-legacy-m2m',
  )
  const keapAddonsWrapperAnnual = apps.find(
    ({code}) =>
      code === 'addon-5-keap-addons-annual' ||
      code === 'addon-5-keap-addons-legacy-annual',
  )

  if (keapAddonsWrapperMonthly) {
    const keapAddonChildren = apps
      .filter(
        ({code}) =>
          appChildToParentMap.get(code) === keapAddonsWrapperMonthly.code,
      )
      .map(resolveChildQuantity)
    childSubscriptions.push({
      code: keapAddonsWrapperMonthly.code,
      quantity: 1,
      addons: keapAddonChildren,
    })
  }
  if (keapAddonsWrapperAnnual) {
    const keapAddonChildren = apps
      .filter(
        ({code}) =>
          appChildToParentMap.get(code) === keapAddonsWrapperAnnual.code,
      )
      .map(resolveChildQuantity)
    childSubscriptions.push({
      code: keapAddonsWrapperAnnual.code,
      quantity: 1,
      addons: keapAddonChildren,
    })
  }

  // ── Marketplace subscription with nested addons ───────────────────────
  // These codes must always be nested inside appmarket addons, never standalone
  const MARKETPLACE_ADDON_CODES = new Set([
    'addon-5-signatures_usage',
    'app-websitebuilder',
    'addon-socialadsbronze-5-m2m',
    'addon-socialadssilver-5-m2m',
    'addon-socialadsgold-5-m2m',
  ])

  const MARKETPLACE_EXCLUDE = new Set([
    'appmarket-5-thryv-m2m',
    'addon-5-keap-addons',
    'addon-5-keap-addons-legacy-m2m',
    'addon-5-keap-addons-annual',
    'addon-5-keap-addons-legacy-annual',
    'proservices-m2m',
    'tp_add-5-thryv-m2m',
    '247support-m2m',
    'addon-5-listrefrsh-m2m',
    'addon-5-stratcontent-m2m',
    'addon-5-leadslift-m2m',
    'addon-5-leadslift-bni-m2m',
    'vert_mc_clientsvcs-5-thryv-m2m',
    'vert_mc_web_clientsvcs-5-thryv-m2m',
    'vert_clientsvcs-5-thryv-m2m',
    'vert_svcs-5-thryv-m2m',
    'vert_mc_clientsvcs-5-thryv-ot',
    'vert_mc_web_clientsvcs-5-thryv-ot',
    'vert_clientsvcs-5-thryv-ot',
    'seobase-5-thryv-m2m',
    'seobase-5-thryv-bni-nt-m2m',
    'seombg-5-thryv-m2m',
    'seo-5-thryv-m2m',
    ...MARKETPLACE_ADDON_CODES,
  ])

  if (marketplace) {
    // Collect marketplace-addon-only items present in the proposal
    const forcedMarketplaceAddons = apps
      .filter(({code}) => MARKETPLACE_ADDON_CODES.has(code))
      .map(({code, quantity = 1, isAlreadyAdded}) => ({
        code,
        quantity,
        ...(isAlreadyAdded && {isAlreadyAdded: true}),
      }))

    // Apps whose parentProduct explicitly points to the marketplace
    const marketplaceChildren = apps
      .filter(({code}) => appChildToParentMap.get(code) === marketplace.code)
      .map(({code, quantity = 1, isAlreadyAdded}) => ({
        code,
        quantity,
        ...(isAlreadyAdded && {isAlreadyAdded: true}),
      }))

    const regularAddons = apps
      .filter(
        ({code}) =>
          !MARKETPLACE_EXCLUDE.has(code) &&
          !code.startsWith('tl-') &&
          !appChildToParentMap.has(code),
      )
      .map(({code, quantity = 1, isAlreadyAdded}) => ({
        code,
        quantity,
        ...(isAlreadyAdded && {isAlreadyAdded: true}),
      }))

    // Merge and deduplicate (forced addons take precedence).
    // marketplace.entitlements holds addons that were seeded from an existing
    // Recurly subscription (e.g. addon-5-ai-webbuilder carried over from
    // Recurly) — mapItemsToCentersApps converts CENTER item.addons to
    // entitlements, not addons, so marketplace.addons is always undefined here.
    // Include entitlements so carried-over marketplace addons are not dropped.
    const seenCodes = new Set()
    const mergedAddons = [
      ...forcedMarketplaceAddons,
      ...(marketplace.entitlements ?? marketplace.addons ?? []),
      ...marketplaceChildren,
      ...regularAddons,
    ].filter(({code}) => {
      if (seenCodes.has(code)) return false
      seenCodes.add(code)
      return true
    })
    // check if const marketPlaceAddon present
    if (!mergedAddons.some(item => item.code === 'addon-5-signatures_usage')) {
      mergedAddons.push({code: 'addon-5-signatures_usage', quantity: 1})
    }
    if (!mergedAddons.some(item => item.code === 'app-websitebuilder')) {
      mergedAddons.push({code: 'app-websitebuilder', quantity: 1})
    }

    childSubscriptions = childSubscriptions.map(item => {
      if (item.code === 'appmarket-5-thryv-m2m') {
        item.addons = mergedAddons
      }
      return item
    })
  }

  // ── Standalone apps (Thryv Leads, SEO variants, vertical, etc.) ──────
  for (const app of apps) {
    // Skip child apps — they are nested under their parent's addons below
    if (appChildToParentMap.has(app.code)) continue

    if (app.code.startsWith('tl-') || app.name === 'Thryv Leads') {
      const tlStartDate = app.startDate || app.configuration?.billingDate
      childSubscriptions.push({
        code: app.code,
        quantity: app.quantity ?? 1,
        addons: [],
        ...(tlStartDate && {startDate: tlStartDate}),
        ...(app.isAlreadyAdded && {isAlreadyAdded: true}),
      })
    } else if (app.code === 'seobase-5-thryv-m2m') {
      // AEO & SEO — standalone childSubscription with proposal customField
      const seoStartDate = app.configuration?.billingDate
      let appQuantity =
        app.unitPrice ||
        (app.quantity ?? 1) * (app.configuration?.proposalAmount ?? 1)
      const seoChildren = apps
        .filter(({code}) => appChildToParentMap.get(code) === app.code)
        .map(resolveChildQuantity)
      childSubscriptions.push({
        code: app.code,
        quantity: appQuantity,
        addons: [...(app.addons ?? []), ...seoChildren],
        ...(seoStartDate && {startDate: seoStartDate}),
        ...(app.configuration &&
          Object.keys(app.configuration).length > 0 && {
            customField: app.configuration,
          }),
        ...(app.isAlreadyAdded && {isAlreadyAdded: true}),
      })
    } else if (app.code === 'seobase-5-thryv-bni-nt-m2m') {
      // SEO BNI non-traditional — standalone childSubscription; quantity comes
      // directly from the existing Recurly subscription (recurlyQuantity, already
      // resolved into app.quantity by mapItemsToCentersApps).
      childSubscriptions.push({
        code: app.code,
        quantity: app.quantity ?? 1,
        addons: [],
        ...(app.isAlreadyAdded && {isAlreadyAdded: true}),
      })
    } else if (
      app.name?.includes('SEO Non-Guaranteed') ||
      app.name?.includes('SEO Money Back Guarantee')
    ) {
      const formatted = formatApps(apps, ({name}) => name.includes(app.name))
      childSubscriptions.push(formatted[0])
    } else if (
      app.code === 'tp_add-5-thryv-m2m' ||
      app.code === '247support-m2m' ||
      app.code === 'proservices-m2m' ||
      app.code === 'addon-5-listrefrsh-m2m' ||
      app.code === 'addon-5-stratcontent-m2m' ||
      app.code === 'addon-5-leadslift-m2m' ||
      app.code.startsWith('vert_')
    ) {
      const standaloneChildren = apps
        .filter(({code}) => appChildToParentMap.get(code) === app.code)
        .map(resolveChildQuantity)
      childSubscriptions.push({
        code: app.code,
        quantity: app.quantity ?? 1,
        addons: standaloneChildren,
        ...(app.isAlreadyAdded && {isAlreadyAdded: true}),
      })
    }
  }

  const oneTimeFees = oneTimes.map(({code, quantity = 1}) => ({
    code,
    quantity,
  }))

  const mcCenter = centers.find(
    ({name, code}) =>
      name?.toLowerCase().includes('marketing') || code?.startsWith('mc_'),
  )
  const mcDomain = mcCenter?.configuration?.domain ?? mcCenter?.domain

  const tlCenter = [...centers, ...apps].find(({code}) =>
    ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
      code,
    ),
  )
  const tlDomain = tlCenter?.configuration?.tlDomain ?? null

  const keapCenter = centers.find(
    ({code}) =>
      KEAP_FAMILY_CODES.includes(code) ||
      KEAP_FAMILY_ANNUAL_CODES.includes(code),
  )
  const keapDomain = keapCenter?.configuration?.domain ?? keapCenter?.domain

  const keapTrailId =
    keapCenter?.configuration?.keapFreeTrialAppId ??
    keapCenter?.keapFreeTrialAppId

  const seoProposal = centers.find(
    ({name, code}) =>
      name.includes('SEO') &&
      name !== 'AEO & SEO' &&
      !SOCIAL_ADS_BNI_PLAN_CODES.includes(code),
  )
  const alphaSeo =
    centers.find(({name}) => name === 'AEO & SEO') ||
    apps.find(({name}) => name === 'AEO & SEO')

  const newAlphaSEO = apps.find(({name}) => name === 'AEO & SEO')

  const subscriptionCustomFields = {
    isCohort: false,
    keapAccount: false,
    keapId: '',
    isInvoiceBilled: false,
    ...(mcDomain ? {mcDomain} : {}),
    ...(keapDomain ? {keapDomain} : {}),
    ...(keapTrailId ? {keapTrailId} : {}),
    ...(tlDomain ? {tlDomain} : {}),
  }

  if (alphaSeo) {
    subscriptionCustomFields.seoProposalId = alphaSeo.configuration?.proposalId
    subscriptionCustomFields.seoDomain = alphaSeo.configuration?.domain
  } else if (newAlphaSEO) {
    subscriptionCustomFields.seoProposalId =
      newAlphaSEO.configuration?.proposalId
    subscriptionCustomFields.seoDomain = newAlphaSEO.configuration?.domain
  }

  if (seoProposal) {
    subscriptionCustomFields.seoProposalId =
      seoProposal.configuration?.proposalId
    subscriptionCustomFields.seoDomain = seoProposal.configuration?.domain
  }

  const tlApp = apps.find(
    app => app.code.startsWith('tl-') || app.name === 'Thryv Leads',
  )
  if (tlApp) {
    const tlThryvLeadsId = readConfigField(tlApp.configuration, 'thryvLeadsId')
    const tlCohortEaid = readConfigField(tlApp.configuration, 'cohortEaid')
    if (tlThryvLeadsId) subscriptionCustomFields.thryvLeadsId = tlThryvLeadsId
    if (tlCohortEaid) {
      subscriptionCustomFields.cohortEaid = tlCohortEaid
      subscriptionCustomFields.isCohort = true
    }
  }

  return {
    parentSubscriptions,
    childSubscriptions,
    oneTimeFees,
    subscriptionCustomFields,
  }
}

/**
 * Convenience wrapper for the Proposal Tool and core OOE's send-link flow:
 * maps raw flat items (a location's items in either flow) straight into the
 * cc/order/send-link payload fields (parentSubscriptions, childSubscriptions,
 * oneTimeFees, and subscriptionCustomFields — isCohort, keapAccount, keapId,
 * isInvoiceBilled, and any of mcDomain/keapDomain/keapTrailId/tlDomain/
 * seoProposalId/seoDomain/thryvLeadsId/cohortEaid that apply).
 *
 * @param {Array} items - flat items (productType/productCode/
 *   configuredOptions/parentProduct)
 * @returns {object} the same {parentSubscriptions, childSubscriptions,
 *   oneTimeFees, subscriptionCustomFields} shape returned by
 *   buildSubscriptionGroups
 */
export function buildSendlinkSubscriptionGroups(items) {
  const {centers, apps, oneTimes} = mapItemsToCentersApps(items)
  return buildSubscriptionGroups({centers, apps, oneTimes})
}
